import { z } from 'zod';
import type { DocumentDto, DocumentKindDto, DocumentScope } from './documents.js';
import { microsoft365GraphId } from './microsoft365.js';

/**
 * Microsoft 365 part 3: documents copied from OneDrive, SharePoint and Outlook attachments (DOC-017…, D-220…).
 * No model tool: the person picks files in the UI ("+" › Microsoft 365, MsBelgeSec; "Belgelerime kaydet" on an
 * attachment card, MsMailEkKaydet; Belgeler › "Belge yükle ▾"). Library imports are copied once by the worker
 * (documents stage `importing`) and keep where they came from (`document_sources`, migration 0023); "Bu sohbet"
 * copies one file at a time like a chat upload. Every item is checked again with the person's own token, so a key
 * only ever reads what that person can already read. No URL leaves the gateway: names only.
 */

export const microsoft365Source = z.enum(['onedrive', 'sharepoint', 'outlook']);
export type Microsoft365Source = z.infer<typeof microsoft365Source>;

/**
 * Graph's drive ids (business drives look like "b!…"), item ids and site ids ("host,guid,guid"). An item id made only
 * of dots is refused: in a request's path "." and ".." are "this folder" and "the folder above" to a URL
 * (/drives/{d}/items/../content is /drives/{d}/content); no other allowed id can be read that way (no "%", no "/").
 */
export const microsoft365DriveId = z.string().regex(/^[A-Za-z0-9!_-]{1,200}$/);
export const microsoft365ItemId = z.string().regex(/^(?!\.+$)[A-Za-z0-9!_.-]{1,200}$/);
export const microsoft365SiteId = z.string().regex(/^[a-z0-9.-]{1,253},[0-9a-f-]{36},[0-9a-f-]{36}$/i);

/** One file: a drive item (OneDrive, SharePoint, a shared item's own drive) or a mail's attachment. */
export const microsoft365SourceKey = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('drive'), driveId: microsoft365DriveId, itemId: microsoft365ItemId }).strict(),
  z.object({ kind: z.literal('attachment'), messageId: microsoft365GraphId, attachmentId: microsoft365GraphId }).strict(),
]);
export type Microsoft365SourceKey = z.infer<typeof microsoft365SourceKey>;

/** A view of the picker: My OneDrive's root, the sites, recent mails with attachments, a site's libraries, a folder. */
export const microsoft365BrowseAt = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('onedrive') }).strict(),
  z.object({ kind: z.literal('sites') }).strict(),
  z.object({ kind: z.literal('outlook') }).strict(),
  z.object({ kind: z.literal('site'), siteId: microsoft365SiteId }).strict(),
  /** itemId null: the library's (or drive's) root */
  z.object({ kind: z.literal('folder'), driveId: microsoft365DriveId, itemId: microsoft365ItemId.nullable() }).strict(),
]);
export type Microsoft365BrowseAt = z.infer<typeof microsoft365BrowseAt>;

/** at most this many files in one import (MsBelgeSec: "Bir seferde en çok 20 dosya") */
export const MICROSOFT365_IMPORT_MAX = 20;

// eslint-disable-next-line no-control-regex
const NO_CONTROLS = /^[^\u0000-\u001f\u007f]*$/;
const searchWords = z.string().trim().min(1).max(200).regex(NO_CONTROLS, 'no control characters');

/** POST /api/microsoft365/files/browse: search words and keys travel in the body, never in a URL. */
export const microsoft365FilesBrowseBody = z
  .object({
    at: microsoft365BrowseAt,
    /** search in this view (OneDrive: also the files shared with the person; sites: site names) */
    query: searchWords.optional(),
    /** the next page: the cursor the same view gave, sealed by the gateway for this person and view */
    cursor: z.string().min(1).max(4000).optional(),
  })
  .strict();
export type Microsoft365FilesBrowseBody = z.infer<typeof microsoft365FilesBrowseBody>;

/**
 * "Nereye eklensin?": Kişisel (default), a team's or workspace's documents ("Ekip: Finans", "Çalışma alanı: …") or
 * Herkes (super admins only). A shared place needs the warning's "Anladım" (the server refuses it otherwise).
 */
export const microsoft365ImportTarget = z.discriminatedUnion('scope', [
  z.object({ scope: z.literal('user') }).strict(),
  z.object({ scope: z.literal('workspace'), workspaceId: z.uuid(), acknowledged: z.literal(true) }).strict(),
  z.object({ scope: z.literal('organization'), acknowledged: z.literal(true) }).strict(),
]);
export type Microsoft365ImportTarget = z.infer<typeof microsoft365ImportTarget>;

const keyOf = (key: Microsoft365SourceKey): string => (key.kind === 'drive' ? `d:${key.driveId}:${key.itemId}` : `a:${key.messageId}:${key.attachmentId}`);

/** One picked file: Outlook items are attachments; OneDrive and SharePoint items are drive items. */
export const microsoft365ImportItem = z
  .object({ source: microsoft365Source, key: microsoft365SourceKey })
  .strict()
  .refine((item) => (item.source === 'outlook') === (item.key.kind === 'attachment'), { path: ['key'], message: 'Outlook items are attachments; OneDrive and SharePoint items are drive items' });
export type Microsoft365ImportItem = z.infer<typeof microsoft365ImportItem>;

/** POST /api/microsoft365/imports: up to 20 files into one place; the worker copies them (stage importing). */
export const microsoft365ImportBody = z
  .object({
    items: z
      .array(microsoft365ImportItem)
      .min(1)
      .max(MICROSOFT365_IMPORT_MAX)
      .refine((items) => new Set(items.map((item) => keyOf(item.key))).size === items.length, 'each file once'),
    target: microsoft365ImportTarget,
    /** a pick from a conversation's composer: the documents are linked to that conversation */
    conversationId: z.uuid().optional(),
  })
  .strict();
export type Microsoft365ImportBody = z.infer<typeof microsoft365ImportBody>;

/** POST /api/conversations/:id/microsoft365-files ("Bu sohbet"): one file, copied now like a chat upload. */
export const microsoft365ConversationFileBody = microsoft365ImportItem;

/** POST /api/uploads/microsoft365: the same on the home page, before its conversation exists (D-93's draft). */
export const microsoft365UploadFileBody = z
  .object({ workspaceId: z.uuid(), draftId: z.uuid().optional(), source: microsoft365Source, key: microsoft365SourceKey })
  .strict()
  .refine((item) => (item.source === 'outlook') === (item.key.kind === 'attachment'), { path: ['key'], message: 'Outlook items are attachments; OneDrive and SharePoint items are drive items' });

/** POST /api/conversations/:id/microsoft365-attachments/:ref/save ("Belgelerime kaydet"; the ref is resolved on the server). */
export const microsoft365SaveAttachmentBody = z.object({ target: microsoft365ImportTarget }).strict();

/** POST /api/documents/source-check: the documents on screen whose source the viewer may check with their own account. */
export const documentSourceCheckBody = z.object({ ids: z.array(z.uuid()).min(1).max(50) }).strict();

// ---- answers -------------------------------------------------------------------------------------------

/**
 * Why a row cannot be picked (the picker shows it greyed with the reason). `source_off`: its source is switched off;
 * `microsoft_scopes_required`: its source is on but the account has not granted that permission yet ("Yeni izin
 * gerekiyor · yeniden bağlan"; OneDrive's search also finds files shared from SharePoint).
 */
export type Microsoft365FileBlock = 'unsupported' | 'too_large' | 'malware' | 'package' | 'source_off' | 'microsoft_scopes_required' | 'empty';

/** One row of the picker's table (Ad · Değiştiren · Değiştirilme · Boyut). Names only: no URL of any kind. */
export interface Microsoft365FileRowDto {
  kind: 'folder' | 'file' | 'site' | 'library';
  name: string;
  /** "Değiştiren"; in Outlook the mail's sender */
  by: string | null;
  /** "Değiştirilme" (ISO); in Outlook the mail's date */
  at: string | null;
  /** bytes; null for folders, sites and libraries */
  size: number | null;
  /** a folder's items ("48 öğe") */
  childCount: number | null;
  /** the type badge (XLS, PDF, DOC, PPT…); null when not a file */
  fileKind: DocumentKindDto | null;
  /** null: it can be picked */
  blocked: Microsoft365FileBlock | null;
  /** an item someone else shared with the person (found by OneDrive's search) */
  shared: boolean;
  /** where it is, for search results and Outlook ("OneDrive › Projeler", the mail's subject) */
  context: string | null;
  /** a file: what an import names */
  key: Microsoft365SourceKey | null;
  /** a folder, a site or a library: the view a click opens */
  open: Microsoft365BrowseAt | null;
}

export interface Microsoft365BrowseDto {
  at: Microsoft365BrowseAt;
  /** "SharePoint › Finans › Belgeler" (nav aria-label="Konum"); at null: not a view of its own */
  crumbs: { name: string; at: Microsoft365BrowseAt | null }[];
  rows: Microsoft365FileRowDto[];
  /** the next page ("Daha fazla göster"), sealed by the gateway; null at the end */
  cursor: string | null;
  /** the SharePoint start: the person's followed sites ("SİTELER"); null elsewhere */
  sites: { name: string; at: Microsoft365BrowseAt }[] | null;
}

/** Why one picked file was not added (MsDurumlar "Belge alınamadı"); a duplicate is not a failure. */
export const MICROSOFT365_IMPORT_FAILURES = [
  'forbidden',
  'not_found',
  'too_large',
  'unsupported',
  'malware',
  'not_a_file',
  'empty',
  'source_off',
  'microsoft_scopes_required',
  'unavailable',
  'busy',
] as const;
export type Microsoft365ImportFailure = (typeof MICROSOFT365_IMPORT_FAILURES)[number];

/** The import's answer: per picked file (`index` into the request's items), added (or already there) or why not. */
export interface Microsoft365ImportResultDto {
  added: { index: number; name: string; document: DocumentDto; duplicate: boolean }[];
  failed: { index: number; name: string | null; code: Microsoft365ImportFailure }[];
}

/** An attachment of this conversation already saved with "Belgelerime kaydet" (and still visible to the viewer). */
export interface Microsoft365SavedAttachmentDto {
  ref: string;
  documentId: string;
  scope: DocumentScope;
  workspaceId: string | null;
}

/**
 * "Kaynakta değişti", checked per viewer with their own account (never stored; a 10-minute cache): same, changed,
 * gone (deleted or moved), forbidden (this viewer cannot see it in Microsoft 365).
 */
export interface DocumentSourceCheckDto {
  id: string;
  state: 'same' | 'changed' | 'gone' | 'forbidden';
  modifiedAt: string | null;
  modifiedBy: string | null;
}

/**
 * `documents.error_code` of a copy that could not be made (the worker; Belgeler "Alınamadı · Neden?"). Missing
 * scopes are `microsoft_scopes_required` here too (one code everywhere).
 */
export const DOCUMENT_SOURCE_ERRORS = [
  'source_forbidden',
  'source_not_found',
  'source_too_large',
  'source_unsupported',
  'source_malware',
  'source_empty',
  'source_off',
  'source_not_connected',
  'microsoft_scopes_required',
  'source_unavailable',
] as const;
export type DocumentSourceError = (typeof DOCUMENT_SOURCE_ERRORS)[number];

// ---- the gateway's files routes (internal) ----------------------------------------------------------------

/**
 * POST /v1/microsoft365/files (the API's token): a picker view, or one file's facts before an import (the
 * preflight: the switch by drive type, the grant, file only, no malware or package, size, type).
 */
export const microsoft365GatewayFilesBody = z.discriminatedUnion('op', [
  z.object({ op: z.literal('browse'), at: microsoft365BrowseAt, query: searchWords.optional(), cursor: z.string().min(1).max(4000).optional() }).strict(),
  z.object({ op: z.literal('item'), source: microsoft365Source, key: microsoft365SourceKey, maxBytes: z.number().int().min(1) }).strict(),
  /** "Kaynakta değişti": a drive item's current cTag and last change, with the viewer's own token */
  z.object({ op: z.literal('check'), key: microsoft365SourceKey, ctag: z.string().max(300).nullable() }).strict(),
]);
export type Microsoft365GatewayFilesBody = z.infer<typeof microsoft365GatewayFilesBody>;

/** The facts of one file (op item; the import's document_sources row is made from them). */
export interface Microsoft365ItemFactsDto {
  /** a shared item: its own drive's ids (remoteItem) */
  key: Microsoft365SourceKey;
  /** by the drive's type: business → onedrive, documentLibrary → sharepoint; attachments → outlook */
  source: Microsoft365Source;
  name: string;
  size: number;
  contentType: string | null;
  fileKind: DocumentKindDto | null;
  /** null: it can be copied */
  blocked: Microsoft365FileBlock | null;
  ctag: string | null;
  modifiedAt: string | null;
  modifiedBy: string | null;
  siteId: string | null;
  siteName: string | null;
  /** "SharePoint › Finans › Belgeler", from names */
  path: string | null;
  /** Outlook: the mail's sender ("Can Aydın") */
  senderName: string | null;
}

/**
 * POST /v1/microsoft365/file (the worker's and the API's token): a drive item's bytes, metadata first; 304 when
 * `ifNoneMatch` is still the item's cTag. Answer headers: x-oppy-name, x-oppy-modified-by and x-oppy-path
 * URI-encoded; x-oppy-content-type, x-oppy-size, x-oppy-ctag, x-oppy-modified-at, x-oppy-drive-type.
 */
export const microsoft365GatewayFileBody = z
  .object({
    driveId: microsoft365DriveId,
    itemId: microsoft365ItemId,
    maxBytes: z.number().int().min(1),
    ifNoneMatch: z.string().min(1).max(300).optional(),
  })
  .strict();
export type Microsoft365GatewayFileBody = z.infer<typeof microsoft365GatewayFileBody>;
